import React, { useMemo } from "react";
import {
  ResponsiveContainer, PieChart, Pie, Cell, Tooltip, Legend,
  LineChart, Line, XAxis, YAxis,
  BarChart, Bar,
} from "recharts";
import {
  Users, Car, Package, AlertOctagon, Shield, Brain, Eye,
} from "lucide-react";

const GROUP_COLOR = {
  Humans:   "hsl(var(--primary))",
  Vehicles: "hsl(var(--warning))",
  Objects:  "hsl(var(--destructive))",
};

const fmtInt = (n) => Number(n ?? 0).toLocaleString("en-IN");

const Pill = ({ icon: Icon, label, value, tone = "default", sub, testid }) => {
  const map = {
    default: "text-foreground",
    success: "sev-low",
    danger:  "sev-critical",
    warn:    "sev-medium",
    primary: "text-[hsl(var(--primary))]",
  };
  return (
    <div className="panel-elevated p-2.5 flex items-center gap-2" data-testid={testid}>
      <Icon className={`w-4 h-4 shrink-0 ${map[tone]}`} />
      <div className="min-w-0">
        <div className="micro-label leading-none">{label}</div>
        <div className={`font-display font-black text-lg leading-tight ${map[tone]}`} data-testid={`${testid}-value`}>
          {value}
        </div>
        {sub && <div className="micro-label text-[9px] mt-0.5 text-muted-foreground">{sub}</div>}
      </div>
    </div>
  );
};

const ClassificationDonut = ({ groups = [] }) => {
  const total = groups.reduce((s, g) => s + g.count, 0);
  if (total === 0) {
    return <div className="h-40 flex items-center justify-center text-[11px] text-muted-foreground">No detections yet.</div>;
  }
  return (
    <div className="relative h-44" data-testid="detection-classification-donut">
      <ResponsiveContainer width="100%" height="100%">
        <PieChart>
          <Pie
            data={groups}
            dataKey="count"
            nameKey="label"
            innerRadius={48}
            outerRadius={72}
            paddingAngle={1}
            isAnimationActive={false}
          >
            {groups.map((g) => (
              <Cell key={g.label} fill={GROUP_COLOR[g.label]} stroke="hsl(var(--surface))" strokeWidth={1.5} />
            ))}
          </Pie>
          <Tooltip
            contentStyle={{ background: "hsl(var(--surface-elevated))", border: "1px solid hsl(var(--border))", fontSize: 11, padding: 6, borderRadius: 2 }}
            formatter={(v, n) => [fmtInt(v), n]}
          />
        </PieChart>
      </ResponsiveContainer>
      <div className="absolute inset-0 flex flex-col items-center justify-center pointer-events-none">
        <div className="text-[10px] text-muted-foreground">Total</div>
        <div className="font-display font-black text-xl leading-none">{fmtInt(total)}</div>
      </div>
    </div>
  );
};

const SubClassesList = ({ rows = [] }) => {
  const groups = useMemo(() => {
    const buckets = { Humans: [], Vehicles: [], Objects: [] };
    rows.forEach((r) => {
      if (buckets[r.group]) buckets[r.group].push(r);
    });
    return buckets;
  }, [rows]);

  return (
    <div className="space-y-2.5" data-testid="detection-subclass-list">
      {Object.entries(groups).map(([group, items]) => {
        const total = items.reduce((s, i) => s + i.count, 0);
        if (total === 0) return null;
        const color = GROUP_COLOR[group];
        return (
          <div key={group}>
            <div className="flex items-center justify-between text-[10px] mb-1">
              <span className="inline-flex items-center gap-1 text-foreground font-medium">
                <span className="w-2 h-2" style={{ background: color }} />
                {group}
              </span>
              <span className="font-mono text-muted-foreground">{fmtInt(total)}</span>
            </div>
            <div className="space-y-0.5">
              {items.map((it) => {
                const pct = total ? Math.round((it.count / total) * 100) : 0;
                return (
                  <div key={it.key} className="flex items-center gap-2 text-[10px]" data-testid={`detection-sub-${it.key}`}>
                    <span className="w-20 truncate text-muted-foreground">{it.label}</span>
                    <div className="flex-1 h-1.5 bg-[hsl(var(--muted))] relative">
                      <div
                        className="absolute inset-y-0 left-0"
                        style={{ width: `${pct}%`, background: color, opacity: 0.7 }}
                      />
                    </div>
                    <span className="w-12 text-right font-mono text-muted-foreground">{fmtInt(it.count)}</span>
                  </div>
                );
              })}
            </div>
          </div>
        );
      })}
    </div>
  );
};

const DetectionTimeline = ({ data = [] }) => {
  const series = data.map((d) => ({
    t: new Date(d.ts).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" }),
    Humans:   d.humans,
    Vehicles: d.vehicles,
    Objects:  d.objects,
  }));
  return (
    <div className="h-44" data-testid="detection-timeline">
      <ResponsiveContainer width="100%" height="100%">
        <LineChart data={series} margin={{ top: 8, right: 8, left: -16, bottom: 0 }}>
          <XAxis dataKey="t" stroke="hsl(var(--muted-foreground))" fontSize={9} tickLine={false} interval="preserveStartEnd" />
          <YAxis stroke="hsl(var(--muted-foreground))" fontSize={9} tickLine={false} width={32} />
          <Tooltip
            contentStyle={{ background: "hsl(var(--surface-elevated))", border: "1px solid hsl(var(--border))", fontSize: 11, padding: 6, borderRadius: 2 }}
            formatter={(v) => fmtInt(v)}
          />
          <Legend iconType="square" wrapperStyle={{ fontSize: 10 }} />
          <Line type="monotone" dataKey="Humans"   stroke={GROUP_COLOR.Humans}   strokeWidth={1.75} dot={false} isAnimationActive={false} />
          <Line type="monotone" dataKey="Vehicles" stroke={GROUP_COLOR.Vehicles} strokeWidth={1.75} dot={false} isAnimationActive={false} />
          <Line type="monotone" dataKey="Objects"  stroke={GROUP_COLOR.Objects}  strokeWidth={1.75} dot={false} isAnimationActive={false} />
        </LineChart>
      </ResponsiveContainer>
    </div>
  );
};

const ConfidenceHistogram = ({ data = [], avg, samples }) => {
  const series = data.map((b) => ({ bucket: b.bucket, count: b.count }));
  return (
    <div className="space-y-1" data-testid="detection-confidence-hist">
      <div className="flex items-center justify-between text-[10px] text-muted-foreground">
        <span>Distribution (% confidence)</span>
        <span className="font-mono">
          Avg <span className="text-foreground">{((avg ?? 0) * 100).toFixed(1)}%</span>
          <span className="ml-2">N {fmtInt(samples)}</span>
        </span>
      </div>
      <div className="h-40">
        <ResponsiveContainer width="100%" height="100%">
          <BarChart data={series} margin={{ top: 8, right: 6, left: -22, bottom: 0 }}>
            <XAxis dataKey="bucket" stroke="hsl(var(--muted-foreground))" fontSize={9} tickLine={false} />
            <YAxis stroke="hsl(var(--muted-foreground))" fontSize={9} tickLine={false} width={26} />
            <Tooltip contentStyle={{ background: "hsl(var(--surface-elevated))", border: "1px solid hsl(var(--border))", fontSize: 11, padding: 6, borderRadius: 2 }} />
            <Bar dataKey="count" radius={[1, 1, 0, 0]} isAnimationActive={false}>
              {series.map((d) => {
                // Color by quality of bucket
                const idx = data.findIndex((b) => b.bucket === d.bucket);
                const colors = [
                  "hsl(var(--destructive))",
                  "hsl(var(--destructive))",
                  "hsl(var(--warning))",
                  "hsl(var(--warning))",
                  "hsl(var(--primary))",
                  "hsl(var(--success))",
                ];
                return <Cell key={d.bucket} fill={colors[idx] || "hsl(var(--primary))"} />;
              })}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
};

const ZoneDetectionTable = ({ rows = [] }) => (
  <div className="overflow-x-auto">
    <table className="w-full text-[11px]" data-testid="detection-zone-table">
      <thead>
        <tr className="text-left font-mono text-[10px] text-muted-foreground border-b border-border">
          <th className="py-1.5 px-2 font-medium">Zone</th>
          <th className="py-1.5 px-2 font-medium text-right">Humans</th>
          <th className="py-1.5 px-2 font-medium text-right">Vehicles</th>
          <th className="py-1.5 px-2 font-medium text-right">Objects</th>
          <th className="py-1.5 px-2 font-medium text-right">Alerts</th>
        </tr>
      </thead>
      <tbody>
        {rows.map((r) => (
          <tr
            key={r.zone_id}
            className="border-b border-border last:border-b-0 hover:bg-[hsl(var(--surface-elevated))]"
            data-testid={`detection-zone-row-${r.zone_id}`}
          >
            <td className="py-1.5 px-2">
              <div className="truncate">{r.zone_name}</div>
              <div className="micro-label text-[8.5px] text-muted-foreground capitalize">{r.type}</div>
            </td>
            <td className="py-1.5 px-2 text-right font-mono text-[hsl(var(--primary))]">{fmtInt(r.humans)}</td>
            <td className="py-1.5 px-2 text-right font-mono sev-medium">{fmtInt(r.vehicles)}</td>
            <td className="py-1.5 px-2 text-right font-mono sev-critical">{fmtInt(r.objects)}</td>
            <td className={`py-1.5 px-2 text-right font-mono ${r.alerts > 0 ? "sev-critical font-bold" : "text-muted-foreground"}`}>
              {r.alerts}
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  </div>
);

export const DetectionIntelligencePanel = ({ data, headerExtra }) => {
  const d = data || {
    kpis: {},
    classification_groups: [],
    sub_classes: [],
    timeline: [],
    zone_rows: [],
    confidence_hist: [],
    confidence_avg: 0,
    confidence_samples: 0,
  };
  const k = d.kpis || {};

  return (
    <section className="space-y-3" data-testid="dashboard-section-detection">
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <div className="flex items-baseline gap-2">
          <h2 className="font-display font-bold text-xs">Detection Intelligence</h2>
          <span className="micro-label">AI Vision · Real-Time Classifications</span>
        </div>
        {headerExtra}
      </div>

      {/* KPI strip */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-2">
        <Pill icon={Eye}           label="Total Detections"   value={fmtInt(k.total_detections)}                          tone="primary" testid="det-kpi-total" />
        <Pill icon={Users}         label="Human Count"        value={fmtInt(k.humans)}                                    tone="primary" testid="det-kpi-humans" />
        <Pill icon={Car}           label="Vehicle Count"      value={fmtInt(k.vehicles)}                                  tone="warn"    testid="det-kpi-vehicles" />
        <Pill icon={Package}       label="Suspicious Objects" value={fmtInt(k.suspicious_objects)}                        tone="danger"  testid="det-kpi-suspicious" />
        <Pill icon={Shield}        label="Intrusion Attempts" value={fmtInt(k.intrusion_attempts)}                        tone="danger"  testid="det-kpi-intrusion" />
        <Pill icon={Brain}         label="AI Accuracy"        value={`${(k.detection_accuracy ?? 0).toFixed?.(1) ?? 0}%`} tone="success" testid="det-kpi-accuracy" />
      </div>

      {/* Donut + Sub-classes */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-3">
        <div className="panel p-3">
          <div className="h-widget mb-2">Object Classification</div>
          <ClassificationDonut groups={d.classification_groups} />
          <div className="flex items-center justify-center gap-3 mt-1 flex-wrap">
            {d.classification_groups.map((g) => (
              <span key={g.label} className="inline-flex items-center gap-1 text-[10px] font-mono text-muted-foreground" data-testid={`det-legend-${g.label.toLowerCase()}`}>
                <span className="w-2 h-2" style={{ background: GROUP_COLOR[g.label] }} />
                {g.label} · {fmtInt(g.count)}
              </span>
            ))}
          </div>
        </div>

        <div className="panel p-3 lg:col-span-2">
          <div className="h-widget mb-2">Detection Sub-Classes</div>
          <SubClassesList rows={d.sub_classes} />
        </div>
      </div>

      {/* Timeline + Confidence histogram */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
        <div className="panel p-3">
          <div className="h-widget mb-2">Detection Timeline</div>
          <DetectionTimeline data={d.timeline} />
        </div>
        <div className="panel p-3">
          <div className="h-widget mb-2 flex items-center gap-1">
            <AlertOctagon className="w-3 h-3" /> AI Confidence Distribution
          </div>
          <ConfidenceHistogram data={d.confidence_hist} avg={d.confidence_avg} samples={d.confidence_samples} />
        </div>
      </div>

      {/* Zone-wise table */}
      <div className="panel p-3">
        <div className="flex items-center justify-between mb-2">
          <div className="h-widget">Zone-Wise Detection</div>
          <span className="micro-label">Sorted By Activity</span>
        </div>
        <ZoneDetectionTable rows={d.zone_rows} />
      </div>
    </section>
  );
};

/**
 * Compact preview used on the Main Dashboard.
 * Shows KPI strip + classification donut + CTA to /detection.
 * The full Sub-Classes / Timeline / Confidence / Zone-Table view lives on /detection.
 */
export const DetectionPreviewPanel = ({ data, onOpen }) => {
  const d = data || { kpis: {}, classification_groups: [] };
  const k = d.kpis || {};
  return (
    <section className="space-y-3" data-testid="dashboard-section-detection-preview">
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <div className="flex items-baseline gap-2">
          <h2 className="font-display font-bold text-xs">Detection Intelligence</h2>
        </div>
        <button
          onClick={onOpen}
          className="btn-tac !px-2 !py-1 text-[10px]"
          data-testid="detection-open-page-btn"
        >
          Open Detection Intel →
        </button>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-2">
        <Pill icon={Eye}     label="Total Detections"   value={fmtInt(k.total_detections)}                          tone="primary" testid="det-kpi-total" />
        <Pill icon={Users}   label="Human Count"        value={fmtInt(k.humans)}                                    tone="primary" testid="det-kpi-humans" />
        <Pill icon={Car}     label="Vehicle Count"      value={fmtInt(k.vehicles)}                                  tone="warn"    testid="det-kpi-vehicles" />
        <Pill icon={Package} label="Suspicious Objects" value={fmtInt(k.suspicious_objects)}                        tone="danger"  testid="det-kpi-suspicious" />
        <Pill icon={Shield}  label="Intrusion Attempts" value={fmtInt(k.intrusion_attempts)}                        tone="danger"  testid="det-kpi-intrusion" />
        <Pill icon={Brain}   label="AI Accuracy"        value={`${(k.detection_accuracy ?? 0).toFixed?.(1) ?? 0}%`} tone="success" testid="det-kpi-accuracy" />
      </div>

      <div className="panel p-3">
        <div className="flex items-center justify-between mb-2">
          <div className="h-widget">Object Classification</div>
          <span className="micro-label">Last Snapshot</span>
        </div>
        <ClassificationDonut groups={d.classification_groups} />
        <div className="flex items-center justify-center gap-3 mt-1 flex-wrap">
          {(d.classification_groups || []).map((g) => (
            <span
              key={g.label}
              className="inline-flex items-center gap-1 text-[10px] font-mono text-muted-foreground"
              data-testid={`det-legend-${g.label.toLowerCase()}`}
            >
              <span className="w-2 h-2" style={{ background: GROUP_COLOR[g.label] }} />
              {g.label} · {fmtInt(g.count)}
            </span>
          ))}
        </div>
      </div>
    </section>
  );
};

export default DetectionIntelligencePanel;
